import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { useFormikContext } from "formik";
import { sortBy } from "@common/helpers/collections";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { COUNTRIES } from "@common/countries";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    EntityProfileFormSchema,
    ProfileFormSchema,
} from "@components/onboarding/useEntityProfileFormConfig";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import {
    annualRevenueBrackets,
    entityTypeItems,
    isEntityProfileType,
} from "../constants";
import { IndustryPicker } from "./IndustryPicker";
import fieldSpacing from "./fieldSpacing.module.scss";

const preferredCountries = ["US", "CA", "GB"];

const countryItems = sortBy(COUNTRIES, [
    (country) =>
        preferredCountries.includes(country.code)
            ? preferredCountries.indexOf(country.code)
            : 100,
    (country) => country.name,
]).map(({ code, name }) => ({ value: code, label: name }));

const countryNameByCode = new Map(
    COUNTRIES.map(({ code, name }) => [code, name]),
);

const fields = createFormikFields<EntityProfileFormSchema>();

// Only the onboarding profile form, which holds the phone number and source, shows them.
const profileFields =
    createFormikFields<
        Pick<ProfileFormSchema, "unverifiedPhoneNumber" | "source">
    >();

const annualRevenueOptions = annualRevenueBrackets.map((value) => ({
    value,
    label: value,
}));

interface Props {
    hidePhoneNumberInput?: boolean;
    isOnboardingFlow?: boolean;
}

export const EntityProfileFormFields = <T extends EntityProfileFormSchema>({
    hidePhoneNumberInput,
    isOnboardingFlow = false,
}: Props) => {
    const { values } = useFormikContext<T>();
    const workspace = useActiveWorkspace();
    const isFreshbooksCustomer = !!workspace?.isFreshbooksCustomer;
    const shouldShowHowDidYouHearField =
        isOnboardingFlow && !isFreshbooksCustomer;

    const industries = values.industries ?? [];

    const isOtherIndustry = industries.includes(EntityProfileIndustry.OTHER);

    return (
        <>
            <fields.Text
                name="name"
                label="Business name"
                size="xl"
                autoComplete="organization"
                className={fieldSpacing.inputField}
            />

            {!hidePhoneNumberInput && (
                <profileFields.Phone
                    name="unverifiedPhoneNumber"
                    label="Phone number"
                    size="sm"
                    className={fieldSpacing.inputField}
                />
            )}

            <GridRow>
                <GridCol sm={6}>
                    <FormikField<EntityProfileType>
                        name="legalType"
                        label="Entity type"
                        control="button"
                        className={fieldSpacing.buttonField}
                    >
                        {({ helpers, field, invalid }) => (
                            <Picker.Select
                                items={entityTypeItems}
                                value={
                                    field.value &&
                                    isEntityProfileType(field.value)
                                        ? field.value
                                        : null
                                }
                                onValueChange={(value) => {
                                    if (value) {
                                        helpers.setValue(value);
                                    }
                                }}
                                placeholder="Select"
                                aria-invalid={invalid}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                        block
                                    />
                                }
                            />
                        )}
                    </FormikField>
                </GridCol>

                <GridCol sm={6}>
                    <IndustryPicker
                        industriesFieldName="industries"
                        industryOtherFieldName="industryOther"
                    />
                </GridCol>
            </GridRow>

            {isOtherIndustry && (
                <fields.Text
                    name="industryOther"
                    label="What industry are you in?"
                    optional
                    size="xl"
                    className={fieldSpacing.inputField}
                />
            )}

            <FormikField<string>
                name="country"
                label="Where does your business pay taxes?"
                control="button"
                className={fieldSpacing.buttonField}
            >
                {({ helpers, field, invalid }) => (
                    <Picker.Select
                        searchable
                        searchPlaceholder="Search..."
                        emptyLabel="No countries found"
                        items={countryItems}
                        value={
                            countryNameByCode.has(field.value)
                                ? field.value
                                : null
                        }
                        onValueChange={(value) => {
                            if (value) {
                                helpers.setValue(value);
                            }
                        }}
                        placeholder="Select country"
                        aria-invalid={invalid}
                        trigger={
                            <Picker.Button variant="primary" size="xl" block />
                        }
                    />
                )}
            </FormikField>

            <GridRow>
                <GridCol sm={shouldShowHowDidYouHearField ? 6 : 12}>
                    <fields.Select
                        name="annualRevenue"
                        label="Annual revenue"
                        className={fieldSpacing.buttonField}
                        options={annualRevenueOptions}
                        placeholder="Select"
                        buttonProps={{ size: "xl", block: true }}
                    />
                </GridCol>
                {shouldShowHowDidYouHearField && (
                    <GridCol sm={6}>
                        <profileFields.Text
                            name="source"
                            label="How did you hear about Kick?"
                            size="xl"
                            className={fieldSpacing.inputField}
                        />
                    </GridCol>
                )}
            </GridRow>
        </>
    );
};
